import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X, RotateCcw, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdminApi, STATUS_STYLES, selectCls, btnPrimary, btnGhost, cardCls } from "./useAdminApi";
import { WalkInDialog } from "./WalkInDialog";
import { errMsg, fmtDate, inr } from "@/lib/api";

const STATUSES = ["CONFIRMED", "PENDING_PAYMENT", "COMPLETED", "CANCELLED"];

const RowActions = ({ a, setStatus }) => (
  <div className="flex gap-1.5 justify-end">
    {a.status === "CONFIRMED" && <button className={btnGhost} onClick={() => setStatus(a, "COMPLETED")} data-testid={`appt-complete-${a.booking_ref}`}><Check className="h-3.5 w-3.5" />Done</button>}
    {["CONFIRMED", "PENDING_PAYMENT"].includes(a.status) && <button className={`${btnGhost} hover:bg-red-50 text-red-600`} onClick={() => setStatus(a, "CANCELLED")} data-testid={`appt-cancel-${a.booking_ref}`}><X className="h-3.5 w-3.5" />Cancel</button>}
    {a.status === "CANCELLED" && a.amount_paid > 0 && <button className={btnGhost} onClick={() => setStatus(a, "CONFIRMED")} data-testid={`appt-restore-${a.booking_ref}`}><RotateCcw className="h-3.5 w-3.5" />Restore</button>}
  </div>
);

export const AppointmentsPanel = ({ onChange }) => {
  const call = useAdminApi();
  const [date, setDate] = useState("");
  const [status, setStatusFilter] = useState("");
  const [rows, setRows] = useState(null);
  const [walkIn, setWalkIn] = useState(false);

  const load = useCallback(() => {
    call("get", "/admin/appointments", null, { date: date || undefined, status: status || undefined }).then(setRows).catch((e) => toast.error(errMsg(e)));
  }, [call, date, status]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (a, s) => {
    try {
      await call("patch", `/admin/appointments/${a.id}/status`, { status: s });
      toast.success(`${a.booking_ref} marked ${s.toLowerCase()}`);
      load(); onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className={`${cardCls} mt-4`} data-testid="appointments-panel">
      <div className="flex flex-wrap items-end gap-3 justify-between mb-5">
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-ink-muted">Date<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-xl mt-1 w-44" data-testid="appt-filter-date" /></label>
          <label className="text-xs text-ink-muted flex flex-col">Status
            <select value={status} onChange={(e) => setStatusFilter(e.target.value)} className={`${selectCls} mt-1`} data-testid="appt-filter-status">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </label>
          {(date || status) && <button className={btnGhost} onClick={() => { setDate(""); setStatusFilter(""); }} data-testid="appt-filter-clear">Clear</button>}
        </div>
        <button className={btnPrimary} onClick={() => setWalkIn(true)} data-testid="walkin-open-button"><Plus className="h-4 w-4" />Walk-in / Phone booking</button>
      </div>
      {!rows ? <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : rows.length === 0 ? (
        <p className="text-center text-ink-muted py-10 text-sm" data-testid="appt-empty">No appointments match these filters.</p>
      ) : (
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="w-full text-sm min-w-[760px]" data-testid="appointments-table">
            <thead><tr className="text-left text-xs text-ink-muted border-b">
              {["Date & time", "Patient", "Reason", "Status", "Paid", ""].map((h) => <th key={h} className="py-2 px-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 align-top" data-testid={`appt-row-${a.booking_ref}`}>
                  <td className="py-3 px-3"><p className="font-medium">{fmtDate(a.appointment_date, { day: "numeric", month: "short", weekday: "short" })}</p><p className="text-ink-muted">{a.slot_label}</p></td>
                  <td className="py-3 px-3"><p className="font-medium">{a.patient_name}</p><a href={`tel:+91${a.patient_phone}`} className="text-teal">+91 {a.patient_phone}</a>{a.notes && <p className="text-xs text-ink-muted mt-1 max-w-[220px]">“{a.notes}”</p>}</td>
                  <td className="py-3 px-3 max-w-[220px]"><p>{a.visit_reason_title}</p><p className="text-xs text-ink-muted">{a.booking_ref} · {a.source || "online"}</p></td>
                  <td className="py-3 px-3"><span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[a.status]}`} data-testid={`appt-status-${a.booking_ref}`}>{a.status.replace("_", " ")}</span>{a.needs_refund && <p className="text-[11px] text-red-600 mt-1">Needs refund</p>}{a.sms_status?.clinic && <p className={`text-[11px] mt-1 ${a.sms_status.clinic === "sent" ? "text-ink-muted" : "text-red-600"}`} data-testid={`appt-clinic-sms-${a.booking_ref}`}>Clinic SMS {a.sms_status.clinic}</p>}{a.reminder_status && <p className="text-[11px] text-ink-muted mt-1" data-testid={`appt-reminder-${a.booking_ref}`}>WhatsApp reminder {a.reminder_status}</p>}{a.confirmed_via === "webhook" && <p className="text-[11px] text-ink-muted mt-1">Confirmed by Razorpay webhook</p>}{a.reschedule_count > 0 && <p className="text-[11px] text-teal mt-1" data-testid={`appt-rescheduled-${a.booking_ref}`}>Rescheduled by patient ×{a.reschedule_count}</p>}{a.cancel_reason === "Cancelled by patient" && <p className="text-[11px] text-red-600 mt-1" data-testid={`appt-refund-${a.booking_ref}`}>Cancelled by patient{a.refund_status === "to_review" ? " · refund to review" : ""}</p>}</td>
                  <td className="py-3 px-3">{inr(a.amount_paid)}</td>
                  <td className="py-3 px-3"><RowActions a={a} setStatus={setStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <WalkInDialog open={walkIn} onOpenChange={setWalkIn} onCreated={() => { load(); onChange(); }} />
    </div>
  );
};
